/**
 * MetaPill — small "GST ready" / "WhatsApp-native" / "14-day free trial" chip.
 *
 * Appears in the prototype on every onboarding screen as part of `form-meta`,
 * and is reused in callout strips elsewhere. The tone drives the dot color; the
 * chip body itself stays neutral to avoid color-coding noise.
 */
import * as React from "react";
import { cn } from "../lib/cn.js";

/**
 * `neutral` is for a label that is **taxonomy, not state** — a "Pre-book" marker
 * beside an order number, a pack-composition chip. Everything coloured reads as
 * a status, and on screens that already carry a status pill a second coloured
 * dot invites "is this one too?". Asked for twice (design 14's composition
 * pills, design 18's pre-book marker) before being added.
 */
export type MetaPillTone = "sky" | "ok" | "warn" | "bad" | "neutral";

export interface MetaPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Color of the leading dot. Default `sky` — every existing call site relies on it. */
  tone?: MetaPillTone | undefined;
}

const dotByTone: Record<MetaPillTone, string> = {
  sky: "bg-accent",
  ok: "bg-ok",
  warn: "bg-warn",
  bad: "bg-bad",
  neutral: "bg-line-2",
};

export function MetaPill({ tone = "sky", className, children, ...props }: MetaPillProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-1 rounded-full",
        "bg-raised text-ink-2 text-xs font-medium",
        className,
      )}
      {...props}
    >
      <span aria-hidden className={cn("w-1.5 h-1.5 rounded-full", dotByTone[tone])} />
      {children}
    </span>
  );
}
